"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.resolvePressableTokens = exports.resolvePressableState = exports.getPressHandlersWithArgs = exports.getCursorStyle = void 0;
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _pressProps = _interopRequireDefault(require("./props/pressProps"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * @typedef {import('react').ReactNode} ReactNode
 */

// These roles should result in cursor: pointer but don't in current RNW releases
const shouldUseCursor = ['checkbox', 'radio', 'switch'];

/**
 * React Native Web has some built-in logic for applying cursor styles based on accessibility roles;
 * however, it misses certain cases. This fills in known cases where widely used versions of RNW
 * fail to apply an expected cursor style.
 *
 * @param {object} props
 * @param {boolean} [props.inactive]
 * @param {boolean} [props.disabled]
 * @param {string} [props.accessibilityRole]
 * @returns
 */
const getCursorStyle = _ref => {
  let {
    inactive,
    disabled,
    accessibilityRole
  } = _ref;
  if (_Platform.default.OS !== 'web') return undefined;
  if (inactive || disabled) return staticStyles.notAllowed;
  if (shouldUseCursor.includes(accessibilityRole)) return staticStyles.pointer;
  return undefined; // allows React Native Web's built-in logic to apply
};

/**
 * @typedef {{ pressed: boolean, focused: boolean, hovered?: boolean }} PressableState
 */

/**
 * Maps the state object given by the React Native `Pressable` component to the set of
 * equivalent appearance names used in UDS.
 *
 * @param {PressableState} pressableState - state object passed by React Native's `<Pressable>` into
 *   render functions passed to its `style` or `children` props.
 * @param {object} [additionalState] - properties specific to the current component,
 *   such as `inactive`, `selected`, etc.
 * @returns {object}
 */
exports.getCursorStyle = getCursorStyle;
const resolvePressableState = function () {
  let {
    pressed = false,
    focused = false,
    hovered = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let additionalState = arguments.length > 1 ? arguments[1] : undefined;
  return {
    ...additionalState,
    focus: focused,
    hover: hovered,
    pressed
  };
};

/**
 * Takes a UDS `tokens` prop and, if it is a function, resolves it based on a state
 * object passed from the React Native `Pressable` component and optional extra properties.
 *
 * @param {object|function} tokens - UDS system tokens prop
 * @param {PressableState} pressableState - state object passed by React Native's `<Pressable>`
 * @param {object} [additionalState] - properties specific to the current component
 * @returns {object} - resolved tokens object
 */
exports.resolvePressableState = resolvePressableState;
const resolvePressableTokens = (tokens, pressableState, additionalState) => {
  if (typeof tokens !== 'function') return tokens;
  const udsPressableState = resolvePressableState(pressableState, additionalState);
  return tokens(udsPressableState);
};
exports.resolvePressableTokens = resolvePressableTokens;
const staticStyles = _StyleSheet.default.create(_Platform.default.select({
  web: {
    notAllowed: {
      cursor: 'not-allowed'
    },
    pointer: {
      cursor: 'pointer'
    }
  },
  default: {}
}));

/**
 * From an object of props, extracts any platform-appropriate press handler functions and wraps
 * them in a function that passes in some provided args. Allows components containing a Pressable
 * to pass in press handlers that are then called with state or values that is otherwise internal.
 */
const getPressHandlersWithArgs = function () {
  let pressableProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let args = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  // Allow handlers to be passed down for blur, hover, focus, pressIn, etc
  const pressHandlers = Object.fromEntries(Object.entries(_pressProps.default.selectHandlers(pressableProps)).map(_ref2 => {
    let [key, handler] = _ref2;
    return {
      [key]: function () {
        for (var _len = arguments.length, defaultArgs = new Array(_len), _key = 0; _key < _len; _key++) {
          defaultArgs[_key] = arguments[_key];
        }
        // Pass each handler data on this button and current selection
        handler(...args, ...defaultArgs);
      }
    };
  }));
  return pressHandlers;
};
exports.getPressHandlersWithArgs = getPressHandlersWithArgs;